<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useConfig } from 'dashboard/composables/useConfig';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import ModelSelector from './components/ModelSelector.vue';
import CaptainPaywall from 'next/captain/pageComponents/Paywall.vue';

const { t } = useI18n();
const { captainAccessEnabled } = useCaptain();
const { isEnterprise, enterprisePlanName } = useConfig();
const { isOnChatwootCloud } = useAccount();

const captainConfigStore = useCaptainConfigStore();
const { uiFlags } = storeToRefs(captainConfigStore);

const isLoading = computed(() => uiFlags.value.isFetching);

const modelFeatures = computed(() => [
  {
    key: 'editor',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.DESCRIPTION'),
  },
  {
    key: 'assistant',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.ASSISTANT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.ASSISTANT.DESCRIPTION'),
    enterprise: true,
  },
  {
    key: 'copilot',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.DESCRIPTION'),
    enterprise: true,
  },
  {
    key: 'label_suggestion',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.LABEL_SUGGESTION.TITLE'),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.LABEL_SUGGESTION.DESCRIPTION'
    ),
  },
  {
    key: 'document_faq_generation',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.DOCUMENT_FAQ_GENERATION.TITLE'),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.DOCUMENT_FAQ_GENERATION.DESCRIPTION'
    ),
    enterprise: true,
  },
  {
    key: 'conversation_faq_generation',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.CONVERSATION_FAQ_GENERATION.TITLE'),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.CONVERSATION_FAQ_GENERATION.DESCRIPTION'
    ),
    enterprise: true,
  },
  {
    key: 'pdf_faq_generation',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.PDF_FAQ_GENERATION.TITLE'),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.PDF_FAQ_GENERATION.DESCRIPTION'
    ),
    enterprise: true,
  },
  {
    key: 'help_center_article_generation',
    title: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.HELP_CENTER_ARTICLE_GENERATION.TITLE'
    ),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.HELP_CENTER_ARTICLE_GENERATION.DESCRIPTION'
    ),
    enterprise: true,
  },
  {
    key: 'help_center_query_translation',
    title: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.HELP_CENTER_QUERY_TRANSLATION.TITLE'
    ),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.HELP_CENTER_QUERY_TRANSLATION.DESCRIPTION'
    ),
    enterprise: true,
  },
  {
    key: 'audio_transcription',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.AUDIO_TRANSCRIPTION.TITLE'),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.AUDIO_TRANSCRIPTION.DESCRIPTION'
    ),
    enterprise: true,
  },
  {
    key: 'help_center_search',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.HELP_CENTER_SEARCH.TITLE'),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.HELP_CENTER_SEARCH.DESCRIPTION'
    ),
    enterprise: true,
  },
  {
    key: 'maisveloz_faq_learning',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.MAISVELOZ_FAQ_LEARNING.TITLE'),
    description: t(
      'CAPTAIN_SETTINGS.MODEL_CONFIG.MAISVELOZ_FAQ_LEARNING.DESCRIPTION'
    ),
    enterprise: true,
  },
]);

const shouldShowFeature = feature => {
  // Cloud will always see these features as long as captain is enabled
  if (isOnChatwootCloud.value && captainAccessEnabled.value) {
    return true;
  }

  if (feature.enterprise) {
    // if the app is in enterprise mode, then we can show the feature
    // this is not the installation plan, but when the enterprise folder is missing
    return isEnterprise;
  }

  return true;
};

const isFeatureAccessible = feature => {
  // Cloud will always see these features as long as captain is enabled
  if (isOnChatwootCloud.value && captainAccessEnabled.value) {
    return true;
  }

  if (feature.enterprise) {
    // plan is shown, but is it accessible?
    // This ensures that the instance has purchased the enterprise license, and only then we allow
    // access
    return isEnterprise && enterprisePlanName === 'enterprise';
  }

  return true;
};

async function handleModelChange({ feature, model }) {
  try {
    await captainConfigStore.updatePreferences({
      captain_models: { [feature]: model },
    });
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    captainConfigStore.fetch();
  }
}

onMounted(() => {
  captainConfigStore.fetch();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-message="t('CAPTAIN_SETTINGS.NOT_ENABLED')"
    :loading-message="t('CAPTAIN_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('CAPTAIN_SETTINGS.TITLE')"
        :description="t('CAPTAIN_SETTINGS.DESCRIPTION')"
        :link-text="t('CAPTAIN_SETTINGS.LINK_TEXT')"
        icon-name="captain"
        feature-name="captain_billing"
      />
    </template>
    <template #body>
      <div v-if="captainAccessEnabled" class="flex flex-col gap-1">
        <!-- Model Configuration Section -->
        <SectionLayout
          :title="t('CAPTAIN_SETTINGS.MODEL_CONFIG.TITLE')"
          :description="t('CAPTAIN_SETTINGS.MODEL_CONFIG.DESCRIPTION')"
        >
          <div class="grid gap-4">
            <ModelSelector
              v-for="feature in modelFeatures"
              v-show="shouldShowFeature(feature)"
              :key="feature.key"
              :is-allowed="isFeatureAccessible(feature)"
              :feature-key="feature.key"
              :title="feature.title"
              :description="feature.description"
              @change="handleModelChange"
            />
          </div>
        </SectionLayout>
      </div>
      <div v-else>
        <CaptainPaywall />
      </div>
    </template>
  </SettingsLayout>
</template>
